import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { subscribeToUpdates } from 'src/sockets/ContentGenSocket';
import { apiGetContentGenerationRequestsByProject } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/contentGenApis';
import {
  GenerationStatusEnum,
  type GetContentGenerationResponse
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/types';
import { getQueryParamValue } from 'src/utils/app';
import { useSnackbar } from 'src/utils/Hooks';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { useBeaconClientId } from 'src/utils/Hooks/reduxSelectors';
import { useContentGenerationStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/components/ProductTable/store';

const ALL_GENERATIONS_QUERY_KEY = ['allGenerationsService'];
const PAGE_SIZE = 1000; // per requirement

type ProjectId = string;

function isGenerating(status: string) {
  return status === GenerationStatusEnum.enum.GENERATING;
}

type GenItem = { projectId?: string; status: string; profileId?: string; profileVersion?: number };

type ProjectInfo = {
  isGenerating: boolean;
  profileId?: string;
  profileVersion?: number;
};

function computeProjectInfoMap(records: GenItem[]): Map<ProjectId, ProjectInfo> {
  const map = new Map<ProjectId, ProjectInfo>();
  for (const r of records) {
    const projectId = r.projectId || 'unknown';
    if (!map.has(projectId)) {
      map.set(projectId, { 
        isGenerating: false, 
        profileId: r.profileId, 
        profileVersion: r.profileVersion 
      });
    }
    const info = map.get(projectId)!;
    if (isGenerating(r.status)) {
      info.isGenerating = true;
    }
    // Keep profile info from any record (they should all be the same per project)
    if (!info.profileId && r.profileId) {
      info.profileId = r.profileId;
    }
    if (info.profileVersion === undefined && r.profileVersion !== undefined) {
      info.profileVersion = r.profileVersion;
    }
  }
  return map;
}

export function useAllGenerationsService() {
  const { showSnackbar } = useSnackbar();
  const { beaconNavigator } = useBeaconNavigator();
  const userId = useAppSelector((state) => state.user.session?.userId);
  const queryClient = useQueryClient();
  const beaconClientId = useBeaconClientId();
  const { isGeneratingModalOpen } = useContentGenerationStore();

  const retailerIdParam = getQueryParamValue('rid');
  const retailerId = Number(retailerIdParam || 0);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ALL_GENERATIONS_QUERY_KEY,
    queryFn: async () => {
      try {
        // We intentionally pass pageNumber/pageSize like other utilities, even if the type schema omits them.
        if (!userId) return null;

        const res = await apiGetContentGenerationRequestsByProject({
          retailerId,
          createdBy: userId,
          pageNumber: 1,
          pageSize: PAGE_SIZE
        });
        return res.data;
      } catch (error) {
        console.error('Error fetching content generation requests:', error);
        return null;
      }
    },
    enabled: Number.isFinite(retailerId) && retailerId > 0 && !!userId,
    refetchOnWindowFocus: false
  });

  const records = useMemo<GenItem[]>(() => {
    const raw = (data?.records as GetContentGenerationResponse['records']) || [];
    return raw.map((r) => ({
      projectId: r.projectId,
      status: (r as any).status || GenerationStatusEnum.enum.NO_GENERATION,
      profileId: r.profileId,
      profileVersion: r.profileVersion
    }));
  }, [data]);

  // Track projects previously in-progress to detect completion transitions
  const prevProjectInfoRef = useRef<Map<ProjectId, ProjectInfo>>(new Map());

  const hasAnyGenerating = useMemo(() => records.some((r) => isGenerating(r.status)), [records]);

  // When a project transitions from generating -> not generating, show snackbar with "View Result"
  // Skip showing the snackbar if the generating modal is still open
  useEffect(() => {
    const currentMap = computeProjectInfoMap(records);
    const prevMap = prevProjectInfoRef.current;

    for (const [projectId, prevInfo] of prevMap.entries()) {
      const currentInfo = currentMap.get(projectId);
      const isNowGenerating = currentInfo?.isGenerating || false;
      if (prevInfo.isGenerating && !isNowGenerating && projectId && projectId !== 'unknown') {
        // Don't show the snackbar if the generating modal is still open or on the review page
        const subtab = getQueryParamValue('subtab');
        if (isGeneratingModalOpen || subtab === BEACON_SUBTABS.PROJECTS_REVIEW || subtab === BEACON_SUBTABS.EXISTING_REVIEW) {
          continue;
        }

        // Get profile info from current map (or fall back to prev)
        const profileId = currentInfo?.profileId || prevInfo.profileId;
        const profileVersion = currentInfo?.profileVersion ?? prevInfo.profileVersion;

        showSnackbar({
          type: 'success',
          message: 'Your project has finished generating. You can now review the results.',
          showButton: true,
          buttonProps: {
            text: 'View Result',
            onClick: () => {
              beaconNavigator.setPathname(`/client/${beaconClientId}/generate`);
              beaconNavigator.updateSearchParams({
                tab: BEACON_TABS.PROJECTS,
                subtab: BEACON_SUBTABS.EXISTING_REVIEW,
                projectId,
                ...(profileId && { profileId }),
                ...(profileVersion !== undefined && { profileVersion: String(profileVersion) })
              });
              beaconNavigator.go();
            }
          }
        });
      }
    }

    // Save current map for next comparison
    prevProjectInfoRef.current = currentMap;
  }, [records, showSnackbar, beaconNavigator, beaconClientId, isGeneratingModalOpen]);

  useEffect(() => {
    if (!userId) return;
    let subscription: { unsubscribe: () => void } | null = null;

    const setup = async () => {
      subscription = await subscribeToUpdates({
        onMessage: (data) => {
          // Only refetch when the update belongs to the current user
          if (data?.createdBy && userId && data.createdBy === userId) {
            refetch();
            // Invalidate and force refetch ContentLibrary queries
            queryClient.invalidateQueries({ 
              queryKey: ['ContentLibrary'],
              refetchType: 'active'
            });
            // Invalidate project review queries
            queryClient.invalidateQueries({ 
              queryKey: ['PROJECT_REVIEW_COMPARE'],
              refetchType: 'active'
            });
            // Invalidate library review queries
            queryClient.invalidateQueries({ 
              queryKey: ['LIBRARY_REVIEW_COMPARE'],
              refetchType: 'active'
            });
            // Invalidate content generation item queries
            queryClient.invalidateQueries({ 
              queryKey: ['ContentGenerationItem'],
              refetchType: 'active'
            });
          }
        },
        onError: (err) => {
          console.error('Content generation socket error in service:', err);
        }
      });
    };

    setup();

    // Cleanup: unsubscribe when component unmounts or dependencies change
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [userId, refetch, queryClient]);
  // No polling; socket-driven refetch only

  const recordsByProject = useMemo(() => {
    const map = new Map<ProjectId, GenItem[]>();
    for (const r of records) {
      const projectId = r.projectId || 'unknown';
      if (!map.has(projectId)) map.set(projectId, []);
      map.get(projectId).push(r);
    }
    return map;
  }, [records]);

  return {
    records,
    recordsByProject,
    isLoading,
    refetch,
    hasAnyGenerating
  };
}

// Mount component to run the service globally
export const ContentGenerationsService: React.FC = () => {
  useAllGenerationsService();
  return null;
};
